import React, { useLayoutEffect, useRef } from 'react';
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { format, isValid, parseISO } from 'date-fns';
import type {
  CarbonAvoidanceStreamRow,
  CarbonCalculationRunAppendix,
  OrganizationCarbonAvoidanceReport,
} from '../../types/carbon-avoidance-report.type';
import {
  carbonMaterialLabel,
  formatEfVersionsUsedForDisplay,
  formatReportPeriodLabel,
  formatRunStatus,
  formatTco2e,
  formatTonnes,
} from '../../utils/carbonAvoidance.util';
import { formatCount } from '../../utils/reportFormatters';
import {
  ReportContentHeader,
  ReportCoverPage,
  ReportPageShell,
} from './ReportChrome';
import { reportMuted } from '../../constants/reportTheme';
import {
  isEsgReportSectionEnabled,
  type EsgReportSectionId,
} from '../../constants/esgReportCustomize.catalog';

const muted: React.CSSProperties = reportMuted;
const cardGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
  marginBottom: 10,
};
const cardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  padding: '10px 12px',
  background: '#fafbfc',
};
const cardLabel: React.CSSProperties = {
  fontSize: '8pt',
  color: '#64748b',
  marginBottom: 4,
  fontWeight: 500,
};
const cardValue: React.CSSProperties = {
  fontSize: '13pt',
  fontWeight: 600,
  color: '#0f172a',
};

const REPORT_TITLE = 'Carbon Avoidance Report';
const RUNS_PER_PAGE = 20;

/** Solid hex colors for PDF rasterization (html2canvas does not reliably render 8-digit hex / SVG pies). */
const PDF_PIE_COLORS = [
  '#0000FE',
  '#3D5A80',
  '#A66A12',
  '#C62828',
  '#6B21A8',
  '#1E4FB8',
  '#7A869A',
  '#9A3412',
];

const pillBase: React.CSSProperties = {
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: '8.5pt',
  fontWeight: 700,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

const byMaterialCard: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  background: '#ffffff',
  padding: 16,
  marginTop: 14,
};

const byMaterialRowShell: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '12px 14px',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
};

const byMaterialCellLabel: React.CSSProperties = {
  fontSize: '8pt',
  color: '#64748b',
  fontWeight: 600,
  marginBottom: 6,
};

type PillTone = 'blue' | 'slate';

function pillColors(tone: PillTone): { bg: string; color: string } {
  if (tone === 'slate') return { bg: '#F1F5F9', color: '#334155' };
  return { bg: '#E3F2FD', color: '#1565C0' };
}

function PdfPill({ label, tone }: { label: string; tone: PillTone }) {
  const { bg, color } = pillColors(tone);
  return <span style={{ ...pillBase, background: bg, color }}>{label}</span>;
}

type ByMaterialIconTheme = {
  iconBg: string;
  iconBorder: string;
  iconColor: string;
};

function byMaterialThemeFor(stream: string | null): ByMaterialIconTheme {
  const normalized = (stream ?? '').toLowerCase();
  if (normalized === 'organic') {
    return {
      iconBg: '#EEF0FF',
      iconBorder: '#C7CCFF',
      iconColor: '#0000CC',
    };
  }
  return {
    iconBg: '#FFF7ED',
    iconBorder: '#FED7AA',
    iconColor: '#9A3412',
  };
}

function ByMaterialStreamIcon({ stream, color }: { stream: string | null; color: string }) {
  const iconStyle: React.CSSProperties = { fontSize: 24, color, display: 'block' };
  const normalized = (stream ?? '').toLowerCase();
  if (normalized === 'organic') {
    return <EnergySavingsLeafOutlinedIcon style={iconStyle} aria-hidden />;
  }
  return <Inventory2OutlinedIcon style={iconStyle} aria-hidden />;
}

function titleCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return value;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  values.forEach((v) => {
    const trimmed = (v ?? '').trim();
    if (!trimmed) return;
    if (!out.includes(trimmed)) out.push(trimmed);
  });
  return out;
}

function summarizeSingleOrMixed(values: Array<string | null | undefined>, mixedLabel: string): string | null {
  const uniq = uniqueNonEmpty(values);
  if (uniq.length === 0) return null;
  if (uniq.length === 1) return uniq[0];
  return mixedLabel;
}

function summarizeEfVersion(labels: Array<string | null | undefined>): string | null {
  const uniq = uniqueNonEmpty(labels);
  if (uniq.length === 0) return null;
  if (uniq.length === 1) return uniq[0];
  const [first] = uniq;
  return `${first} (+${uniq.length - 1})`;
}

function ByMaterialSummaryCard({
  title,
  stream,
  materialLabel,
  avoidedLabel,
  efVersion,
}: {
  title: string;
  stream: string | null;
  materialLabel: string | null;
  avoidedLabel: string;
  efVersion: string | null;
}) {
  const theme = byMaterialThemeFor(stream);

  return (
    <div className="esg-report-avoid-break" style={byMaterialCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            border: `1px solid ${theme.iconBorder}`,
            background: theme.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ByMaterialStreamIcon stream={stream} color={theme.iconColor} />
        </div>
        <div style={{ fontWeight: 800, fontSize: '12pt', color: '#0f172a' }}>{title}</div>
      </div>

      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
        <div style={byMaterialRowShell}>
          <div style={{ paddingRight: 12, borderRight: '1px solid #e2e8f0' }}>
            <div style={byMaterialCellLabel}>Material</div>
            {materialLabel ? (
              <PdfPill label={titleCase(materialLabel)} tone="blue" />
            ) : (
              <div style={{ fontStyle: 'italic', fontWeight: 600, color: '#64748b' }}>Not assigned</div>
            )}
          </div>
          <div style={{ padding: '0 12px', borderRight: '1px solid #e2e8f0' }}>
            <div style={byMaterialCellLabel}>Avoided (tCO2e)</div>
            <div style={{ fontWeight: 800, fontSize: '10pt', color: '#0f172a' }}>{avoidedLabel}</div>
          </div>
          <div style={{ paddingLeft: 12 }}>
            <div style={byMaterialCellLabel}>EF version</div>
            {efVersion ? (
              <PdfPill label={efVersion} tone="slate" />
            ) : (
              <div style={{ color: '#64748b', fontWeight: 800 }}>—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatPiePercent(value: number, total: number): string {
  if (total <= 0 || value <= 0) return '';
  const pct = (value / total) * 100;
  if (pct < 0.05) return '<0.1%';
  if (pct < 0.1) return '0.1%';
  if (pct < 1) return `${pct.toFixed(1)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(0)}%`;
}

function chunkRuns(runs: CarbonCalculationRunAppendix[], size: number): CarbonCalculationRunAppendix[][] {
  const chunks: CarbonCalculationRunAppendix[][] = [];
  for (let i = 0; i < runs.length; i += size) {
    chunks.push(runs.slice(i, i + size));
  }
  return chunks.length ? chunks : [[]];
}

function Section({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="esg-report-avoid-break" style={{ marginBottom: 16 }}>
      <h2
        style={{
          fontSize: '10pt',
          fontWeight: 600,
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: 6,
          marginBottom: intro ? 6 : 10,
        }}
      >
        {title}
      </h2>
      {intro ? (
        <p style={{ ...muted, marginBottom: 10, marginTop: 0, lineHeight: 1.45 }}>{intro}</p>
      ) : null}
      {children}
    </section>
  );
}

function ExecCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="esg-report-avoid-break" style={cardStyle}>
      <div style={cardLabel}>{label}</div>
      <div style={cardValue}>{value}</div>
    </div>
  );
}

function PdfDataTable({
  headers,
  rows,
  compact,
}: {
  headers: string[];
  rows: string[][];
  compact?: boolean;
}) {
  const cellStyle: React.CSSProperties = {
    padding: compact ? '4px 6px' : '6px 8px',
    fontSize: compact ? '8pt' : '9pt',
    verticalAlign: 'top',
  };
  return (
    <table className="esg-report-table esg-report-avoid-break" style={{ width: '100%' }}>
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h} style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td
                key={j}
                style={{
                  ...cellStyle,
                  ...(j === 0 && compact ? { wordBreak: 'break-all', fontFamily: 'monospace' } : {}),
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function drawDonutSlice(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
  color: string
) {
  const sweep = endAngle - startAngle;
  ctx.beginPath();
  if (sweep >= Math.PI * 2 - 0.001) {
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.arc(cx, cy, innerR, Math.PI * 2, 0, true);
  } else {
    ctx.arc(cx, cy, outerR, startAngle, endAngle);
    ctx.arc(cx, cy, innerR, endAngle, startAngle, true);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function StreamPieChart({ streams }: { streams: CarbonAvoidanceStreamRow[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const total = streams.reduce((sum, row) => sum + row.avoided_tco2e, 0) || 1;
  const chartSize = 180;

  const segments = streams.map((row, i) => ({
    row,
    color: PDF_PIE_COLORS[i % PDF_PIE_COLORS.length],
    pctLabel: formatPiePercent(row.avoided_tco2e, total),
  }));

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = 2;
    canvas.width = chartSize * dpr;
    canvas.height = chartSize * dpr;
    canvas.style.width = `${chartSize}px`;
    canvas.style.height = `${chartSize}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = chartSize / 2;
    const cy = chartSize / 2;
    const outerR = (chartSize / 2) * 0.74;
    const innerR = (chartSize / 2) * 0.46;

    ctx.clearRect(0, 0, chartSize, chartSize);

    if (total <= 0 || streams.length === 0) {
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
      ctx.fillStyle = '#e2e8f0';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      return;
    }

    let startAngle = -Math.PI / 2;
    streams.forEach((row, i) => {
      const sweep = (row.avoided_tco2e / total) * Math.PI * 2;
      const endAngle = startAngle + sweep;
      const color = PDF_PIE_COLORS[i % PDF_PIE_COLORS.length];

      drawDonutSlice(ctx, cx, cy, outerR, innerR, startAngle, endAngle, color);

      const pctLabel = formatPiePercent(row.avoided_tco2e, total);
      if (pctLabel && sweep > 0.2) {
        const mid = (startAngle + endAngle) / 2;
        const labelR = (outerR + innerR) / 2;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 11px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(pctLabel, cx + labelR * Math.cos(mid), cy + labelR * Math.sin(mid));
      }

      startAngle = endAngle;
    });
  }, [streams, total]);

  return (
    <div className="esg-report-avoid-break" style={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        width={chartSize}
        height={chartSize}
        style={{ display: 'block', margin: '0 auto' }}
        aria-hidden
      />
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid #e2e8f0',
          fontSize: '8pt',
        }}
      >
        {segments.map((segment, i) => (
          <div
            key={`${segment.row.material_label}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
              color: '#334155',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: segment.color,
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>{carbonMaterialLabel(segment.row.material_label)}</span>
            <span style={{ color: '#64748b', flexShrink: 0 }}>
              {formatTco2e(segment.row.avoided_tco2e)} tCO2e
            </span>
            <span style={{ fontWeight: 600, flexShrink: 0, minWidth: 40, textAlign: 'right' }}>
              {segment.pctLabel || '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The regulatory identity of the load, with the hazard marker that decides whether
 * it needed an assessment. Frozen on the run at calculation time. */
function formatRegulatoryCode(run: CarbonCalculationRunAppendix): string {
  if (!run.regulatory_code) return '—';
  return run.hazard_status ? `${run.regulatory_code} (${run.hazard_status})` : run.regulatory_code;
}

function formatCompletedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return format(d, 'MMM d, yyyy');
}

function MethodologyFooterBlock({ report }: { report: OrganizationCarbonAvoidanceReport }) {
  return (
    <div
      className="esg-report-avoid-break"
      style={{
        marginTop: 16,
        padding: '10px 12px',
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        borderRadius: 4,
        fontSize: '8pt',
        color: '#64748b',
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Methodology</div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        <li style={{ marginBottom: 4 }}>{report.methodology.formula_description}</li>
        <li style={{ marginBottom: 4 }}>
          Based on delivery completion date; only materials with an active, approved emission factor are calculated.
        </li>
        <li style={{ marginBottom: 4 }}>Emission factor versions snapshotted at calculation time.</li>
        <li>
          Avoided emissions are an estimate against a baseline treatment (ISO 14064-2-style). They are reported
          separately from the Scope 1–3 GHG inventory, are never deducted from it, and are not offsets or carbon
          credits.
        </li>
      </ul>
    </div>
  );
}

export interface CarbonAvoidanceReportPdfTemplateProps {
  report: OrganizationCarbonAvoidanceReport | null;
  organizationName: string;
  organizationLogoSrc: string | null;
  periodLabel: string;
  generatedLabel: string;
  containerId?: string;
  includeSections?: readonly EsgReportSectionId[];
}

export function CarbonAvoidanceReportPdfTemplate({
  report,
  organizationName,
  organizationLogoSrc,
  periodLabel,
  generatedLabel,
  containerId = 'esg-report-pdf-carbon',
  includeSections,
}: CarbonAvoidanceReportPdfTemplateProps) {
  const orgName = report?.organization.name ?? organizationName;
  const on = (id: EsgReportSectionId) => isEsgReportSectionEnabled(id, includeSections);

  const pageBodies: Array<{ body: React.ReactNode; methodologyFooter?: React.ReactNode }> = [];

  pageBodies.push({
    body: (
      <ReportCoverPage
        reportTitle={REPORT_TITLE}
        organizationName={orgName}
        organizationLogoSrc={organizationLogoSrc}
        periodLabel={periodLabel}
        generatedLabel={generatedLabel}
        size="compact"
      />
    ),
  });

  if (report) {
    const { executive_summary: summary } = report;
    const runChunks = chunkRuns(report.calculation_runs ?? [], RUNS_PER_PAGE);
    const showExec = on('carbon-exec-summary');
    const showBreakdowns = on('carbon-breakdowns');
    const showAudit = on('carbon-audit');
    const methodologyFooter = on('carbon-methodology') ? (
      <MethodologyFooterBlock report={report} />
    ) : undefined;

    if (showExec || showBreakdowns) {
      pageBodies.push({
        body: (
          <>
            <ReportContentHeader
              title={showExec ? 'Executive summary' : 'Breakdowns'}
              organizationName={orgName}
              organizationLogoSrc={organizationLogoSrc}
              periodLabel={periodLabel}
              generatedLabel={generatedLabel}
              size="compact"
            />
            {showExec ? (
              <div style={cardGrid}>
                <ExecCard
                  label="Avoided emissions (tCO2e)"
                  value={`${formatTco2e(summary.total_avoided_tco2e)} tCO2e`}
                />
                <ExecCard
                  label="Mass in completed calculation runs (t)"
                  value={`${formatTonnes(summary.total_weight_diverted_tonnes)} t`}
                />
                <ExecCard label="Completed runs" value={formatCount(summary.completed_run_count)} />
                <ExecCard label="Skipped runs" value={formatCount(summary.skipped_run_count)} />
              </div>
            ) : null}
            {showBreakdowns ? (
              <>
                <Section title="By material">
                  {report.by_stream.length === 0 ? (
                    <p style={muted}>No stream breakdown for this period.</p>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        alignItems: 'stretch',
                      }}
                    >
                      <PdfDataTable
                        headers={['Catalog material', 'Avoided (tCO2e)', 'Weight (t)', 'EF versions']}
                        rows={report.by_stream.map((row) => [
                          carbonMaterialLabel(row.material_label),
                          formatTco2e(row.avoided_tco2e),
                          formatTonnes(row.weight_tonnes),
                          formatEfVersionsUsedForDisplay(row.emission_factor_versions_used),
                        ])}
                      />
                      <StreamPieChart streams={report.by_stream} />
                    </div>
                  )}
                </Section>
                <Section title="By category">
                  {report.by_material.length === 0 ? (
                    <p style={muted}>No material breakdown for this period.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {report.by_material.map((group) => {
                        const stream =
                          summarizeSingleOrMixed(
                            group.materials.map((m) => m.material_label),
                            'mixed'
                          ) ?? group.category_label;
                        const materialLabel = summarizeSingleOrMixed(
                          group.materials.map((m) => m.material_type),
                          'mixed'
                        );
                        const efVersion = summarizeEfVersion(
                          group.materials.map((m) => m.emission_factor_version_label)
                        );

                        return (
                          <ByMaterialSummaryCard
                            key={group.category_label}
                            title={group.category_label}
                            stream={stream}
                            materialLabel={materialLabel}
                            avoidedLabel={formatTco2e(group.total_avoided_tco2e)}
                            efVersion={efVersion}
                          />
                        );
                      })}
                    </div>
                  )}
                </Section>
              </>
            ) : null}
          </>
        ),
      });
    }

    if (showAudit) {
      runChunks.forEach((chunk, chunkIdx) => {
        pageBodies.push({
          body: (
            <>
              <ReportContentHeader
                title={
                  runChunks.length > 1
                    ? `Calculation runs (${chunkIdx + 1}/${runChunks.length})`
                    : 'Calculation runs'
                }
                organizationName={orgName}
                organizationLogoSrc={organizationLogoSrc}
                periodLabel={periodLabel}
                generatedLabel={generatedLabel}
                size="compact"
              />
              <Section title="Calculation runs">
                {chunk.length === 0 ? (
                  <p style={muted}>No calculation runs in this period.</p>
                ) : (
                  <PdfDataTable
                    compact
                    headers={[
                      'Run ID',
                      'Completion Date',
                      'Status',
                      'Material',
                      'Weight (ton)',
                      'Avoided (tCO2e)',
                      'Energy (kWh)',
                      'EWC code',
                      'EF version',
                    ]}
                    rows={chunk.map((run) => [
                      run.run_id,
                      formatCompletedAt(run.delivery_completed_at),
                      formatRunStatus(run.run_status),
                      run.material_type_name ?? '—',
                      run.weight_tonnes !== null ? formatTonnes(run.weight_tonnes) : '—',
                      run.avoided_tco2e !== null ? formatTco2e(run.avoided_tco2e) : '—',
                      run.energy_saved_kwh !== null
                        ? Math.round(run.energy_saved_kwh).toLocaleString('en-US')
                        : '—',
                      formatRegulatoryCode(run),
                      run.emission_factor?.emission_factor_version_label ?? '—',
                    ])}
                  />
                )}
              </Section>
            </>
          ),
        });
      });
    }

    if (methodologyFooter && pageBodies.length > 0) {
      const last = pageBodies[pageBodies.length - 1];
      pageBodies[pageBodies.length - 1] = { ...last, methodologyFooter };
    }
  }

  const totalPages = pageBodies.length;

  return (
    <div
      id={containerId}
      className="esg-report-screen-root esg-report-pdf-capture-root"
      dir="ltr"
      lang="en"
      aria-hidden
    >
      {pageBodies.map((page, i) => (
        <ReportPageShell
          key={i}
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          pageIndex={i + 1}
          totalPages={totalPages}
          methodologyFooter={page.methodologyFooter}
          compactFooter
        >
          {page.body}
        </ReportPageShell>
      ))}
    </div>
  );
}

export function carbonReportPeriodLabelFromReport(
  report: OrganizationCarbonAvoidanceReport | null,
  fallbackStart: string,
  fallbackEnd: string
): string {
  if (report?.reporting_period) {
    return formatReportPeriodLabel(
      report.reporting_period.start_date,
      report.reporting_period.end_date
    );
  }
  return formatReportPeriodLabel(fallbackStart, fallbackEnd);
}
